import { ChangeDetectionStrategy, Component, OnInit, WritableSignal, AfterViewInit, ElementRef, ViewChildren, QueryList, effect, ChangeDetectorRef } from '@angular/core';
import { UiBulletin } from 'src/app/api/types/mc-dashboard';
import { DataPadService } from 'src/app/ui/data-pad/data-pad.service';
import { IconService } from 'src/app/ui/icon.service';
import { DashboardDataService } from 'src/app/routed-modules/mission-control/services/dashboard-data.service';
import { BaseDashboardComponent } from '../base-dashboard.component';
import { ThemeService } from 'src/app/services/theme.service';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { McDashboardService } from 'src/app/api/mc-dashboard.service';
import { MessageService } from 'primeng/api';
import { ErrorService } from 'src/app/services/error.service';

@Component({
  selector: 'app-pulse',
  templateUrl: './pulse.component.html',
  styleUrls: ['./pulse.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PulseComponent extends BaseDashboardComponent implements OnInit, AfterViewInit {
  bulletins: WritableSignal<UiBulletin[]> = this.createSignal<UiBulletin[]>([]);
  hasAttemptedLoad = this.createSignal<boolean>(false);
  canAddAlert = this.createSignal<boolean>(false);
  @ViewChildren('contentContainer') contentContainers!: QueryList<ElementRef>;
  
  // Read confirmation state
  hasReadCheckbox = this.createSignal<boolean>(false);
  mostRecentBulletinId = this.createSignal<number | null>(null);
  isSubmitting = this.createSignal<boolean>(false);
  hasReadPulse = this.createSignal<boolean>(false);
  readTimestamp = this.createSignal<Date | null>(null);

  constructor(
    private dashboardDataService: DashboardDataService,
    private dataPadService: DataPadService,
    public icons: IconService,
    private themeService: ThemeService,
    private auth: LocalAuthService,
    private cdr: ChangeDetectorRef,
    private mcDashboardService: McDashboardService,
    private messageService: MessageService,
    private errorService: ErrorService
  ) {
    super();
    this.canAddAlert.set(this.auth.hasAnyRole(['it', 'head']));
    
    
    // Use effect to watch the signal
    effect(() => {
      const data = this.dashboardDataService.bulletins();
      
      if (data !== null && data !== undefined) {
        this.bulletins.set(data);
        this.hasAttemptedLoad.set(true);
        this.setLoading(false);
        
        // Set the most recent bulletin ID (first one in the array, ordered by timestamp DESC)
        if (data.length > 0 && data[0].id) {
          const bulletinId = data[0].id;
          this.mostRecentBulletinId.set(bulletinId);
          // Check if user has already read this pulse
          this.checkPulseReadStatus(bulletinId);
          this.cdr.markForCheck(); // Ensure change detection runs
        } else if (data.length > 0) {
          // Log warning if ID is missing
          console.warn('Most recent bulletin missing ID:', data[0]);
        }
        
        // Trigger change detection for OnPush strategy
        this.cdr.markForCheck();
        
        // Schedule rendering after data update and change detection
        setTimeout(() => {
          this.renderContent();
          this.cdr.detectChanges();
        }, 0);
      }
    });

    // Watch for theme changes and re-render content
    effect(() => {
      const themeState = this.themeService.themeState();
      // Re-render content when theme changes
      if (this.hasAttemptedLoad()) {
        setTimeout(() => {
          this.renderContent();
          this.cdr.detectChanges();
        }, 100);
      }
    });
  }

  ngOnInit(): void {
    this.setLoading(true);
    // Initialize data loading
    this.dashboardDataService.initializeData();
  }

  ngAfterViewInit(): void {
    // Fix styling after view is initialized
    setTimeout(() => {
      this.renderContent();
      this.cdr.detectChanges();
    }, 100);
  }

  isMostRecentBulletin(bulletin: UiBulletin): boolean {
    const bulletinId = bulletin.id;
    const mostRecentId = this.mostRecentBulletinId();
    // Compare both IDs, handling undefined/null cases
    return bulletinId !== undefined && bulletinId !== null && 
           mostRecentId !== null && 
           bulletinId === mostRecentId;
  }

  checkPulseReadStatus(alertId: number): void {
    this.mcDashboardService.checkPulseReadStatus(alertId).subscribe({
      next: (response) => {
        this.hasReadPulse.set(response.hasRead);
        this.readTimestamp.set(response.readTimestamp);
        this.cdr.markForCheck();
      },
      error: (error) => {
        // Silently fail - if we can't check, assume not read
        this.hasReadPulse.set(false);
        this.readTimestamp.set(null);
        this.cdr.markForCheck();
      }
    });
  }

  confirmRead(): void {
    try {
      if (!this.hasReadCheckbox()) {
        // Show message that checkbox must be checked
        this.messageService.add({
          severity: 'warn',
          summary: 'Confirmation Required',
          detail: 'Please check the box to confirm you have read the Pelican Pulse.'
        });
        return;
      }

      const alertId = this.mostRecentBulletinId();
      if (!alertId || alertId <= 0) {
        this.errorService.error(
          'Unable to mark pulse as read', 
          new Error('No valid bulletin ID available. Please refresh the page and try again.'), 
          this.messageService
        );
        return;
      }

      // Disable the button while processing
      this.isSubmitting.set(true);
      this.cdr.markForCheck();

      this.mcDashboardService.markPulseAsRead(alertId).subscribe({
        next: (response) => {
          this.isSubmitting.set(false);
          if (response.success) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Thank you for reading the Pelican Pulse!'
            });
            // Mark as read and update timestamp
            this.hasReadPulse.set(true);
            this.readTimestamp.set(new Date());
            // Reset checkbox after successful submission
            this.hasReadCheckbox.set(false);
            this.cdr.markForCheck();
          } else {
            this.errorService.error(
              'Failed to mark pulse as read', 
              new Error(response.message || 'Unknown error occurred. Please try again.'), 
              this.messageService
            );
            this.cdr.markForCheck();
          }
        },
        error: (error) => {
          this.isSubmitting.set(false);
          // Provide user-friendly error message
          const errorMessage = error?.error?.message || error?.message || 'Network error. Please check your connection and try again.';
          this.errorService.error(
            'Error marking pulse as read', 
            new Error(errorMessage), 
            this.messageService
          );
          this.cdr.markForCheck();
        }
      });
    } catch (error) {
      this.isSubmitting.set(false);
      // Handle any unexpected errors in the method itself
      this.errorService.error(
        'Unexpected error', 
        error instanceof Error ? error : new Error('An unexpected error occurred. Please try again.'), 
        this.messageService
      );
      this.cdr.markForCheck();
    }
  }

  addAlert(): void {
    this.dataPadService.showPanel({
      panelName: 'addAlert',
      inputData: {}
    });
    
    // Refresh content periodically to catch new alerts
    // This is a simple approach - in a production app you might want to use a more sophisticated event system
    const refreshInterval = setInterval(() => {
      this.dashboardDataService.initializeData();
      this.renderContent();
      this.cdr.detectChanges();
    }, 2000);
    
    // Clear the interval after 30 seconds to avoid unnecessary polling
    setTimeout(() => {
      clearInterval(refreshInterval);
    }, 30000);
  }


  renderContent(): void {
    const bulletins = this.bulletins();
    if (!bulletins) {
      return;
    }
    
    const containers = this.contentContainers.toArray();
    
    bulletins.forEach((bulletin, index) => {
      if (containers[index]) {
        const container = containers[index].nativeElement;
        // Directly set innerHTML without Angular's sanitization
        container.innerHTML = bulletin.note;
        
        // Apply any necessary styling fixes immediately
        this.fixContentStyling(container);
        
        // Apply styling fixes again after a short delay to ensure they stick
        setTimeout(() => {
          this.fixContentStyling(container);
        }, 50);
      }
    });
  }

  fixContentStyling(container?: HTMLElement): void {
    const containers = container ? [container] : this.contentContainers.toArray().map(ref => ref.nativeElement);
    const isDarkMode = this.themeService.isDarkMode();
    
    containers.forEach((element: HTMLElement) => {
      // Apply dark mode class to the container
      if (isDarkMode) {
        element.classList.add('dark-mode-content');
      } else {
        element.classList.remove('dark-mode-content');
      }

      // Apply mobile-responsive fixes
      this.applyMobileResponsiveFixes(element);

      // Fix teal background for section headers
      const tableCells = element.querySelectorAll('td');
      tableCells.forEach((td: Element) => {
        const cell = td as HTMLElement;
        const hasWhiteText = cell.querySelector('span[style*="color:#ffffff"]') ||
                             cell.querySelector('h2 span[style*="color:#ffffff"]');
        const hasSectionHeader = cell.textContent?.includes('ORGANIZATIONAL LEADERSHIP') ||
                                 cell.textContent?.includes('OPERATIONS') ||
                                 cell.textContent?.includes('HUMAN RESOURCES') ||
                                 cell.textContent?.includes('MARKETING') ||
                                 cell.textContent?.includes('RECRUITING') ||
                                 cell.textContent?.includes('OPERATIONAL STANDARDS') ||
                                 cell.textContent?.includes('CONSULTING');
        
        if (hasWhiteText || hasSectionHeader) {
          // Use theme-aware colors
          const headerBgColor = isDarkMode ? '#1ab394' : '#55c1ac';
          const headerTextColor = '#ffffff';
          
          cell.style.backgroundColor = headerBgColor;
          cell.style.padding = '0.5rem';
          
          const textElements = cell.querySelectorAll('h2, span');
          textElements.forEach((el: Element) => {
            const textElement = el as HTMLElement;
            textElement.style.color = headerTextColor;
            textElement.style.fontWeight = '700';
            textElement.style.fontFamily = 'Montserrat, sans-serif';
          });
        }
      });

      // Fix teal text colors more aggressively
      const tealTextElements = element.querySelectorAll('span[style*="color:#55c1ac"], p[style*="color:#55c1ac"], span[style*="color:#1ab394"], p[style*="color:#1ab394"]');
      tealTextElements.forEach((el: Element) => {
        const textElement = el as HTMLElement;
        const tealColor = isDarkMode ? '#1ab394' : '#55c1ac';
        textElement.style.color = tealColor;
        textElement.style.fontWeight = '700';
      });

      // Fix image/logo display issues
      const images = element.querySelectorAll('img');
      images.forEach((img: Element) => {
        const imageElement = img as HTMLImageElement;
        
        // Check if the image's parent has text-align: center
        const parent = imageElement.parentElement;
        let shouldCenter = false;
        
        // Check parent and all ancestors for text-align: center
        let currentElement = parent;
        while (currentElement && currentElement !== element) {
          const style = currentElement.getAttribute('style');
          if (style && style.includes('text-align: center')) {
            shouldCenter = true;
            break;
          }
          currentElement = currentElement.parentElement;
        }
        
        // Also check if the image itself has centering styles
        const imageStyle = imageElement.getAttribute('style');
        if (imageStyle && (imageStyle.includes('margin: 0 auto') || imageStyle.includes('text-align: center'))) {
          shouldCenter = true;
        }
        
        // Ensure images are responsive and display properly
        imageElement.style.maxWidth = '100%';
        imageElement.style.height = 'auto';
        
         if (shouldCenter) {
           // Center the image
           imageElement.style.display = 'block';
           imageElement.style.margin = '0.5rem auto';
         } else {
          imageElement.style.display = 'block';
          imageElement.style.margin = '0.5rem 0';
        }
        
        // Preserve original styling if it exists
        const originalStyle = imageElement.getAttribute('style');
        if (originalStyle && !originalStyle.includes('max-width')) {
          // Keep original styling but ensure responsiveness
          imageElement.style.maxWidth = '100%';
          imageElement.style.height = 'auto';
        }
        
        // Fix any broken image sources or add fallback
        if (!imageElement.src || imageElement.src === '') {
          console.warn('Image with empty src found in pulse content');
        }
      });

      // Fix list styling to ensure bullet points are visible
      this.fixListStyling(element);
      
      // Preserve and enhance background colors and other styling
      this.preserveBackgroundColors(element, isDarkMode);
      
      // Fix other hardcoded colors for dark mode
      this.fixHardcodedColors(element, isDarkMode);
    });
  }

  private fixListStyling(element: HTMLElement): void {
    // Fix unordered lists to ensure bullet points are visible
    const unorderedLists = element.querySelectorAll('ul');
    unorderedLists.forEach((ul: Element) => {
      const htmlUl = ul as HTMLElement;
      // Force list-style-type to disc for bullet points
      htmlUl.style.listStyleType = 'disc';
      htmlUl.style.paddingLeft = '1.5rem';
      htmlUl.style.marginLeft = '0';
      htmlUl.style.listStylePosition = 'outside';
    });

    // Fix ordered lists to ensure numbers are visible
    const orderedLists = element.querySelectorAll('ol');
    orderedLists.forEach((ol: Element) => {
      const htmlOl = ol as HTMLElement;
      // Force list-style-type to decimal for numbers
      htmlOl.style.listStyleType = 'decimal';
      htmlOl.style.paddingLeft = '1.5rem';
      htmlOl.style.marginLeft = '0';
      htmlOl.style.listStylePosition = 'outside';
    });

    // Fix list items to ensure proper display
    const listItems = element.querySelectorAll('li');
    listItems.forEach((li: Element) => {
      const htmlLi = li as HTMLElement;
      // Force list items to display as list items
      htmlLi.style.display = 'list-item';
      htmlLi.style.listStylePosition = 'outside';
      
      // Preserve any existing list-style-type from inline styles
      const existingStyle = htmlLi.getAttribute('style');
      if (existingStyle && existingStyle.includes('list-style-type')) {
        const listStyleMatch = existingStyle.match(/list-style-type:\s*([^;]+)/);
        if (listStyleMatch) {
          htmlLi.style.listStyleType = listStyleMatch[1].trim();
        }
      } else {
        // If no specific list-style-type, inherit from parent
        const parent = htmlLi.parentElement;
        if (parent) {
          if (parent.tagName.toLowerCase() === 'ul') {
            htmlLi.style.listStyleType = 'disc';
          } else if (parent.tagName.toLowerCase() === 'ol') {
            htmlLi.style.listStyleType = 'decimal';
          }
        }
      }
    });
  }

  private preserveBackgroundColors(element: HTMLElement, isDarkMode: boolean): void {
    // Find elements with background colors and ensure they're preserved
    const elementsWithBg = element.querySelectorAll('*[style*="background-color"]');
    elementsWithBg.forEach((el: Element) => {
      const htmlEl = el as HTMLElement;
      const style = htmlEl.getAttribute('style');
      if (style && style.includes('background-color')) {
        // In dark mode, we might want to adjust background colors for better contrast
        if (isDarkMode) {
          // Keep the background color but ensure text is readable
          const textElements = htmlEl.querySelectorAll('*');
          textElements.forEach((textEl: Element) => {
            const textHtmlEl = textEl as HTMLElement;
            if (!textHtmlEl.style.color) {
              // If no text color is set, ensure it's visible against the background
              textHtmlEl.style.color = '#ffffff';
            }
          });
        }
      }
    });

    // Special handling for table cells with background colors
    const tableCells = element.querySelectorAll('td, th');
    tableCells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;
      const style = htmlCell.getAttribute('style');
      if (style && style.includes('background-color')) {
        // Ensure the background color is preserved
        const bgColorMatch = style.match(/background-color:\s*([^;]+)/);
        if (bgColorMatch) {
          htmlCell.style.backgroundColor = bgColorMatch[1].trim();
          
          // If it's a teal background (#55c1ac or #1ab394), ensure white text
          const bgColor = bgColorMatch[1].trim().toLowerCase();
          if (bgColor === '#55c1ac' || bgColor === '#1ab394') {
            const textElements = htmlCell.querySelectorAll('h1, h2, h3, h4, h5, h6, span, p');
            textElements.forEach((textEl: Element) => {
              const textHtmlEl = textEl as HTMLElement;
              textHtmlEl.style.color = '#ffffff';
              textHtmlEl.style.fontWeight = '700';
            });
          }
        }
      }
    });

    // Special handling for headings with background colors
    const headings = element.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach((heading: Element) => {
      const htmlHeading = heading as HTMLElement;
      const style = htmlHeading.getAttribute('style');
      
      // Check if heading already has background color
      if (style && style.includes('background-color')) {
        // Ensure the background color is preserved
        const bgColorMatch = style.match(/background-color:\s*([^;]+)/);
        if (bgColorMatch) {
          // Force the background color to be applied
          htmlHeading.style.backgroundColor = bgColorMatch[1].trim();
          
          // If it's a teal background, ensure white text
          const bgColor = bgColorMatch[1].trim().toLowerCase();
          if (bgColor === '#55c1ac' || bgColor === '#1ab394') {
            htmlHeading.style.color = '#ffffff';
            htmlHeading.style.fontWeight = '700';
          }
        }
      } else {
        // Check if this heading should have a teal background based on content
        const headingText = htmlHeading.textContent?.toLowerCase() || '';
        const isSectionHeader = headingText.includes('operations') || 
                               headingText.includes('fundraising') || 
                               headingText.includes('human resources') || 
                               headingText.includes('marketing') || 
                               headingText.includes('recruiting') || 
                               headingText.includes('consulting') || 
                               headingText.includes('meetings/admin') ||
                               headingText.includes('ppm365') ||
                               headingText.includes('organizational leadership') ||
                               headingText.includes('operational standards');
        
        if (isSectionHeader) {
          // Apply teal background to section headers
          const headerBgColor = isDarkMode ? '#1ab394' : '#55c1ac';
          htmlHeading.style.backgroundColor = headerBgColor;
          htmlHeading.style.color = '#ffffff';
          htmlHeading.style.fontWeight = '700';
          htmlHeading.style.padding = '0.5rem';
          htmlHeading.style.fontFamily = 'Montserrat, sans-serif';
        }
      }
    });
  }

  private fixHardcodedColors(element: HTMLElement, isDarkMode: boolean): void {
    if (isDarkMode) {
      // Apply dark mode fixes
      this.applyDarkModeFixes(element);
    } else {
      // Reset any previously applied dark mode styles
      this.resetDarkModeStyles(element);
    }
  }

  private applyDarkModeFixes(element: HTMLElement): void {
    // Fix black text that becomes invisible in dark mode
    const blackTextElements = element.querySelectorAll('span[style*="color:#000000"], p[style*="color:#000000"]');
    blackTextElements.forEach((el: Element) => {
      const textElement = el as HTMLElement;
      textElement.style.color = '#e5e7eb'; // Light gray for dark mode
    });

    // Fix white text - keep white in dark mode
    const whiteTextElements = element.querySelectorAll('span[style*="color:#ffffff"], p[style*="color:#ffffff"]');
    whiteTextElements.forEach((el: Element) => {
      const textElement = el as HTMLElement;
      textElement.style.color = '#ffffff'; // Keep white for dark mode
    });

    // Fix teal color to be brighter for dark mode
    const tealElements = element.querySelectorAll('span[style*="color:#55c1ac"], p[style*="color:#55c1ac"]');
    tealElements.forEach((el: Element) => {
      const textElement = el as HTMLElement;
      textElement.style.color = '#1ab394'; // Brighter teal for dark mode
    });

    // Fix links to be light blue for dark mode
    const links = element.querySelectorAll('a');
    links.forEach((link: Element) => {
      const linkElement = link as HTMLElement;
      linkElement.style.color = '#60a5fa'; // Light blue for dark mode
    });
  }

  private resetDarkModeStyles(element: HTMLElement): void {
    // Remove any inline styles that were applied for dark mode
    const allElements = element.querySelectorAll('*');
    allElements.forEach((el: Element) => {
      const htmlElement = el as HTMLElement;
      // Only reset colors that we know we modified
      if (htmlElement.style.color === '#e5e7eb' || 
          htmlElement.style.color === '#1ab394' || 
          htmlElement.style.color === '#60a5fa') {
        htmlElement.style.color = '';
      }
    });
  }

  private applyMobileResponsiveFixes(element: HTMLElement): void {
    // Remove any inline styles that prevent wrapping
    const allElements = element.querySelectorAll('*');
    allElements.forEach((el: Element) => {
      const htmlElement = el as HTMLElement;
      
      // Remove problematic inline styles
      if (htmlElement.style.whiteSpace === 'nowrap' || 
          htmlElement.style.whiteSpace === 'pre' ||
          htmlElement.getAttribute('style')?.includes('white-space:nowrap') ||
          htmlElement.getAttribute('style')?.includes('white-space:pre')) {
        htmlElement.style.whiteSpace = 'normal';
      }
      
      // Force responsive width handling
      if (htmlElement.style.width) {
        const widthValue = htmlElement.style.width;
        if (widthValue.includes('px')) {
          const pixelValue = parseInt(widthValue.replace('px', ''));
          if (pixelValue > 200) { // Lower threshold for mobile
            htmlElement.style.maxWidth = '100%';
            htmlElement.style.width = 'auto';
          }
        }
      }
      
      // Force text wrapping
      htmlElement.style.wordWrap = 'break-word';
      htmlElement.style.overflowWrap = 'break-word';
      htmlElement.style.wordBreak = 'break-word';
      htmlElement.style.hyphens = 'auto';
      htmlElement.style.maxWidth = '100%';
    });

    // Fix any elements with fixed widths that might cause horizontal scrolling
    const elementsWithWidth = element.querySelectorAll('*[style*="width"]');
    elementsWithWidth.forEach((el: Element) => {
      const htmlElement = el as HTMLElement;
      const widthStyle = htmlElement.style.width;
      
      // If width is in pixels and might be too wide for mobile
      if (widthStyle && widthStyle.includes('px')) {
        const widthValue = parseInt(widthStyle.replace('px', ''));
        if (widthValue > 200) { // Lower threshold for mobile
          htmlElement.style.maxWidth = '100%';
          htmlElement.style.width = 'auto';
        }
      }
    });

    // Fix any elements with fixed heights that might cause issues
    const elementsWithHeight = element.querySelectorAll('*[style*="height"]');
    elementsWithHeight.forEach((el: Element) => {
      const htmlElement = el as HTMLElement;
      const heightStyle = htmlElement.style.height;
      
      // If height is in pixels and might be too restrictive
      if (heightStyle && heightStyle.includes('px')) {
        const heightValue = parseInt(heightStyle.replace('px', ''));
        if (heightValue < 50) { // Arbitrary threshold for minimum height
          htmlElement.style.height = 'auto';
          htmlElement.style.minHeight = heightStyle;
        }
      }
    });

    // Ensure all text elements can wrap properly
    const textElements = element.querySelectorAll('p, span, div, td, th, li');
    textElements.forEach((el: Element) => {
      const htmlElement = el as HTMLElement;
      htmlElement.style.wordWrap = 'break-word';
      htmlElement.style.overflowWrap = 'break-word';
      htmlElement.style.wordBreak = 'break-word';
      htmlElement.style.hyphens = 'auto';
      htmlElement.style.whiteSpace = 'normal';
      htmlElement.style.maxWidth = '100%';
    });

    // Fix any tables that might have fixed layouts
    const tables = element.querySelectorAll('table');
    tables.forEach((table: Element) => {
      const tableElement = table as HTMLElement;
      tableElement.style.tableLayout = 'fixed';
      tableElement.style.width = '100%';
      tableElement.style.maxWidth = '100%';
      
      // Fix table cells
      const cells = tableElement.querySelectorAll('td, th');
      cells.forEach((cell: Element) => {
        const cellElement = cell as HTMLElement;
        cellElement.style.wordWrap = 'break-word';
        cellElement.style.overflowWrap = 'break-word';
        cellElement.style.wordBreak = 'break-word';
        cellElement.style.whiteSpace = 'normal';
        cellElement.style.maxWidth = '100%';
      });
    });
  }

  // TrackBy function for better performance
  trackByBulletin(index: number, bulletin: UiBulletin): string {
    return `${bulletin.staffName}-${bulletin.timestamp}-${bulletin.subject}`;
  }

  hasStaffId(staffIds: number[]): boolean {
    const user = this.auth.currentUser;
    return user ? staffIds.includes(user.newstaffId) : false;
  }
}
